import React, { useEffect, useState } from 'react';
import Login from '../Login';
import * as api from '../../lib/api';
import { Toaster } from '../../components/ui/sonner';
import AdminOverview from './AdminOverview';
import AdminUsers from './AdminUsers';
import AdminPayments from './AdminPayments';
import AdminFeedback from './AdminFeedback';
import AdminLogs from './AdminLogs';
import AdminLeads from './AdminLeads';
import { Segmented } from './ui';
import Logo from '../../components/Logo';

// Hidden entry point, mounted by App.js when the URL is /admin — a completely
// separate component tree from the consumer AppShell (see the check in
// App.js), so nothing here can interfere with the regular app's own state.
// Auth reuses the normal email/password Login + JWT (no separate admin
// password) — access itself is gated server-side by ADMIN_EMAILS (see
// backend/admin.py's require_admin), confirmed here by actually calling
// /admin/overview rather than trusting the client.

const TABS = [
  { key: 'overview', label: 'Resumen' },
  { key: 'users', label: 'Usuarios' },
  { key: 'payments', label: 'Pagos' },
  { key: 'leads', label: 'Leads' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'logs', label: 'Logs' },
];

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const check = async () => {
      const token = api.getToken();
      if (!token) { setLoading(false); return; }
      let me = null;
      try {
        me = await api.getMe();
      } catch (e) {
        api.removeToken();
        setLoading(false);
        return;
      }
      setUser(me);
      try {
        await api.getAdminOverview(); // confirms actual admin access, not just "logged in"
      } catch (e) {
        setForbidden(e?.response?.status === 403);
      }
      setLoading(false);
    };
    check();
  }, []);

  const handleLogin = async (loggedInUser) => {
    try {
      await api.getAdminOverview();
      setUser(loggedInUser);
      setForbidden(false);
    } catch (e) {
      setUser(loggedInUser);
      setForbidden(e?.response?.status === 403);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster theme="dark" position="top-right" />
      </>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-center px-4">
        <div>
          <p className="text-lg font-semibold text-white mb-2">No tienes acceso</p>
          <p className="text-sm text-slate-400">Esta cuenta ({user.email}) no está autorizada para ver el panel de administración.</p>
        </div>
      </div>
    );
  }

  // Same system as the consumer app (design_guidelines.json): solid header
  // (no backdrop blur), segmented tabs, whitespace between sections.
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      <header className="border-b border-white/[0.06] bg-[#020617]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Logo size={28} textClassName="text-base" />
            <span className="text-xs font-medium text-slate-500 border-l border-white/10 pl-2.5">Admin</span>
          </div>
          <Segmented options={TABS} value={tab} onChange={setTab} testId="admin-tabs" />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 sm:pt-6">
        {tab === 'overview' && <AdminOverview />}
        {tab === 'users' && <AdminUsers />}
        {tab === 'payments' && <AdminPayments />}
        {tab === 'leads' && <AdminLeads />}
        {tab === 'feedback' && <AdminFeedback />}
        {tab === 'logs' && <AdminLogs />}
      </main>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
